<template>
  <PopupContent
    v-show="showPopup"
    class="bubble-popup"
    :style="popupStyle"
    :show-chart="showPopupChart"
    :popup-details="activePopupDetails"
    @size-change="updatePopupCardSize"
  />
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import PopupContent from '@/components/public/Popup/PopupContent.vue'

const components = {
  PopupContent,
}

const INITIAL_POPUP_CARD_WIDTH = 450

// eslint-disable-next-line no-use-before-define
@Component<BubblePopup>({ components })
export default class BubblePopup extends Vue {
  popupCardHeight: number = 0

  get activePopupDetails () {
    return this.$typedStore.activeVisualisation.activeBubblesPopupDetails
  }

  get showPopup () {
    return this.activePopupDetails !== undefined
  }

  get mainPanelHeight () {
    return this.$typedStore.public.size.mainPanelDimensions.height
  }

  get showPopupChart () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig.showPopupChart
  }

  get popupStyle () {
    return {
      ...this.position?.top ? { top: this.position.top } : {},
      ...this.position?.left ? { left: this.position.left } : {},
      width: INITIAL_POPUP_CARD_WIDTH + 'px',
      transform: `scale(${this.transformCardScale})`,
    }
  }

  get maxAvailableContainerHeight () {
    // 90% of the available height so that the popup should not touch the top or bottom edges
    return this.mainPanelHeight * 0.9
  }

  get transformCardScale () {
    // https://css-tricks.com/scaled-proportional-blocks-with-css-and-javascript
    // The scale is the available length divided by the content length
    // e.g. availableHeight / contentHeight
    if (this.maxAvailableContainerHeight && this.popupCardHeight && this.popupCardHeight > this.maxAvailableContainerHeight) {
      const scale = this.maxAvailableContainerHeight / this.popupCardHeight
      return Math.min(scale, 1)
    }
    return 1
  }

  get position () {
    if (this.activePopupDetails) {
      // #region Calculate the left position of the popup
      const bubbleRadius = this.activePopupDetails.radius
      const scaledPopupWidth = INITIAL_POPUP_CARD_WIDTH * this.transformCardScale
      const scaledPopupWidthDifference = (INITIAL_POPUP_CARD_WIDTH - scaledPopupWidth)
      const horizontalBuffer = 20 * this.$typedStore.public.size.bubblesVisualisationScaleFactor

      // Take the bubble co-ordinates, subtract the bubble radius, the width of the scaled down popup,
      // the difference between the original popup width and the scaled down popup width divided by 2
      // and then apply a buffer between the bubble and the popup
      const left = this.activePopupDetails.position.x - bubbleRadius - scaledPopupWidth - (scaledPopupWidthDifference / 2) - horizontalBuffer

      // #endregion

      // #region Calculate the top position of the popup
      const scaledPopupHeight = this.roundToOne(this.popupCardHeight * this.transformCardScale)
      const scaledPopupHeightDifference = (this.popupCardHeight - scaledPopupHeight)
      const headerHeight = this.mainPanelHeight - this.$typedStore.public.size.bubblesContainerDimensions.height
      const verticalBuffer = 50 * this.$typedStore.public.size.bubblesVisualisationScaleFactor

      let pushDownBy = 0
      if (scaledPopupHeight === this.maxAvailableContainerHeight) {
        // If the card is taking up all of the available container height
        // then push it down so there is equal space above and below the card
        pushDownBy = ((this.mainPanelHeight - scaledPopupHeight) / 2)
      } else {
        // Determine whether to display above or below the bubble

        // The popup can cover the whole mainPanel but the html coordinates are only
        // for the Bubbles area, we need to adjust the vertical coordinate to account
        // for the mainPanel header component
        const verticalAdjustment = (headerHeight / 2)
        const displayBelow = this.activePopupDetails.position.y + verticalAdjustment < this.mainPanelHeight / 2

        if (displayBelow) {
          // Push the popup down so the top aligns with the bubble, taking into account
          // the mainPanel header and any scaling down of the popup
          pushDownBy = this.activePopupDetails.position.y + headerHeight + (scaledPopupHeightDifference / 2) - verticalBuffer
        } else {
          // Push the popup down so the bottom aligns with the bubble, taking into account
          // the mainPanel header and any scaling down of the popup
          pushDownBy = this.activePopupDetails.position.y + headerHeight - scaledPopupHeight + verticalBuffer
        }
      }

      // Ensure the popup does not display too high or too low
      const minPushDown = (this.mainPanelHeight - this.maxAvailableContainerHeight) / 2
      const maxPushDown = this.mainPanelHeight - scaledPopupHeight - minPushDown
      pushDownBy = Math.max(pushDownBy, minPushDown)
      pushDownBy = Math.min(pushDownBy, maxPushDown)

      // Calculate the final top position of the popup
      const top = pushDownBy - (scaledPopupHeightDifference / 2) - headerHeight
      // #endregion

      return { left: `${left}px`, top: `${top}px` }
    }

    return {}
  }

  updatePopupCardSize (newValue: {clientHeight: number}) {
    this.popupCardHeight = newValue.clientHeight
  }

  private roundToOne (num: number) {
    return (Math.round(num * 10) / 10)
  }
}

</script>

<style scoped>

.bubble-popup {
  position: absolute;
  z-index: 2;
  width: 450px;
  opacity: 0.97;
}

</style>
